// @flow
import Spinner from "components/Spinner";
import isNil from "lodash/isNil";
import * as React from "react";
import { useCallback } from "react";
import type { User } from "types/user";
import UI from "ui";
import Pagination from "ui/atoms/Pagination";
import TableEmptyMessage from "ui/table/TableEmptyMessage";
import TeamTableItem from "utils/TeamTableItem";

import { useLoadUsers } from "../../queries/users/useLoadUsers";

type Props = {
  searchTerm: string,
  onEditUser: (user: User) => void,
  onDeleteUser: (userId: number) => void,
  onSendReminder: (userId: number, userEmail: string) => void,
};

function TeamTable({
  searchTerm,
  onEditUser,
  onDeleteUser,
  onSendReminder,
}: Props): React.Node {
  const [ordering, setOrdering] = React.useState([]);
  const [page, setPage] = React.useState(1);
  const {
    data: users,
    isLoading: isUsersLoading,
    isFetching: isUsersFetching,
  } = useLoadUsers({
    page,
    pageSize: 20,
    search: searchTerm,
    ordering: ordering.join(","),
  });

  const isLoading = isUsersLoading || isUsersFetching;

  const getServerPropName = useCallback((propName) => {
    if (propName === "name") {
      return "first_name";
    } else if (propName === "role") {
      return "membership__role";
    }
    return propName;
  }, []);

  const handleOrdering = useCallback(
    (propName) => {
      const _propName = getServerPropName(propName);
      if (ordering.includes(_propName) || ordering.includes(`-${_propName}`)) {
        setOrdering((prev) =>
          prev.map((item) => {
            if (item.includes(_propName)) {
              return item.includes("-") ? _propName : `-${_propName}`;
            }
            if (propName === "name" && item.includes("last_name")) {
              return item.includes("-") ? "last_name" : "-last_name";
            }
            return item;
          }),
        );
      } else {
        if (propName === "name") {
          setOrdering((prev) => [...prev, _propName, "last_name"]);
        } else {
          setOrdering((prev) => [...prev, _propName]);
        }
      }
    },
    [getServerPropName, ordering],
  );

  const getSelectedOrder = useCallback(
    (propName) => {
      const _propName = getServerPropName(propName);

      const order = ordering?.find((item) => item.includes(_propName));
      if (order) {
        return order.includes("-") ? "desc" : "asc";
      }

      return "asc";
    },
    [getServerPropName, ordering],
  );

  return (
    <table className="table-auto min-w-full bg-white">
      <thead>
        <tr className="border-b border-gray-200 bg-gray-50">
          <UI.TableTitle
            onClick={() => handleOrdering("name")}
            propName="name"
            selectedOrder={getSelectedOrder("name")}
            selectedProp="name"
          >
            Name
          </UI.TableTitle>
          <UI.TableTitle
            // style={{ minWidth }}
            onClick={() => handleOrdering("email")}
            propName="email"
            selectedOrder={getSelectedOrder("email")}
            selectedProp="email"
          >
            Email
          </UI.TableTitle>
          <UI.TableTitle
            // style={{ minWidth }}
            onClick={() => handleOrdering("role")}
            propName="role"
            selectedOrder={getSelectedOrder("role")}
            selectedProp="role"
          >
            Role
          </UI.TableTitle>
          <UI.TableTitle
            onClick={() => handleOrdering("is_verified")}
            propName="is_verified"
            selectedOrder={getSelectedOrder("is_verified")}
            selectedProp="is_verified"
          >
            Status
          </UI.TableTitle>
          <UI.TableTitle />
        </tr>
      </thead>
      <tbody>
        {isLoading && (
          <tr>
            <td colSpan={5}>
              <div className="flex justify-center items-center w-full">
                <Spinner color="onLight" size={40} />
              </div>
            </td>
          </tr>
        )}
        {(users?.results?.length || 0) > 0 &&
          users?.results.map((item) =>
            isNil(item?.id) ? null : (
              <TeamTableItem
                item={item}
                key={item.id}
                onDeleteUser={onDeleteUser}
                onEditUser={onEditUser}
                onSendReminder={onSendReminder}
              />
            ),
          )}
        {!isLoading && (users?.results?.length || 0) === 0 && (
          <tr>
            <td colSpan={3}>
              <TableEmptyMessage>No Results Found</TableEmptyMessage>
            </td>
          </tr>
        )}
        <tr>
          <td colSpan={5}>
            <Pagination
              // TODO: Remove conditions after refactoring forms
              nextCallback={() => {
                setPage(page + 1);
              }}
              page={page}
              pageSize={20}
              previousCallback={() => {
                setPage(page - 1);
              }}
              total={users?.count}
            />
          </td>
        </tr>
      </tbody>
    </table>
  );
}

export default TeamTable;
